// ============================================================
// THE TAKE-COVER CUE - a spot's cover against every gun that threatens it (2026-10-25).
//
// The map invites a figure to take cover: a curved screen on its base ring per enemy gun, as tall as the share
// of him the cover hides, and a movement reach shaded by the worst cover each spot would give. This module is
// the arithmetic under both. Every reading is `relateOne` from the THREAT to the figure standing at the spot,
// so the cue can never disagree with the attack that follows (cover near the target, `ignoresTerrain`, hidden
// terrain - all the same call).
//
// Who threatens: the other SIDE (Squad Mode's sides, the user's ruling), in the fight, on the map, holding a
// loaded ranged slot. Range is ignored: a screen is about cover, not reach. A blocked line is "out of sight".
//
// Pure. Zero React dependency.
// ============================================================
import type { Cover } from '../../types';
import type { Combatant } from '../roster/types';
import type { Solid } from '../battlefield/types';
import type { Token } from '../battlefield/tokens';
import type { ReachableTile } from '../battlefield/movement';
import { tileKey } from '../battlefield/movement';
import { refuseRetiredRules, relateOne, type RelationRules } from '../battlefield/relations';
import { firstBlocker } from '../battlefield/lineOfSight';
import { standingAt, tokenEye } from '../battlefield/tokens';
import { bearingBetween } from '../battlefield/facing';
import { opposedSides, sideOf, teamOf } from '../roster/team';
import { isInTheFight } from '../roster/inTheFight';
import { projectLoadout } from '../roster/projection';
import { isObject } from '../bodyShape';

/** A cover level, or out of sight: the line is blocked and the gun cannot see him at all. */
export type CueLevel = Cover['level'] | 'out';

/** One gun that can threaten the figure. */
export interface Threat {
  id: string;
  name: string;
  token: Token;
}

/** What one gun's line says about the figure at one spot. */
export interface ThreatReading {
  threatId: string;
  name: string;
  level: CueLevel;
  /** What gives the cover (or hides him when out of sight); null in the open. */
  source: string | null;
  /** The nearest terrain too far from him to count (cover near the target), for the readout's "why". */
  beyond: { source: string | null; metres: number } | null;
  /** Ground bearing from the figure to the gun, in degrees (0 = +x, 90 = +z): where its screen stands. */
  bearing: number;
}

/** The figure's tokens by id, as the shell holds them. */
export type TokenLookup = Readonly<Record<string, Token | undefined>>;

/** Least to most protected: open is the worst, out of sight the best (a seen figure can still be caught by Blast). */
const CUE_ORDER: Record<CueLevel, number> = { none: 0, low: 1, half: 2, threeQuarter: 3, full: 4, out: 5 };

function holdsRangedWeapon(c: Combatant): boolean {
  return projectLoadout(c).slots.some((slot) => !slot.isEmpty && slot.weaponType === 'ranged');
}

/**
 * Every gun that threatens this figure: on the OTHER side, in the fight, on the map, armed with a ranged weapon.
 * A figure on no team has no side, so nobody threatens it and it wears no screens; a gun on no team threatens
 * nobody. An Object (2026-10-26) has no cover to take: nothing threatens it here, so it never wears screens.
 */
export function threatsTo(figure: Combatant, roster: readonly Combatant[], tokens: TokenLookup): Threat[] {
  if (!sideOf(teamOf(figure)) || isObject(figure.sheet)) return [];
  return roster
    .filter((c) => c.id !== figure.id && !!tokens[c.id] && threatens(c, figure))
    .map((c) => ({ id: c.id, name: c.sheet?.name ?? c.id, token: tokens[c.id] as Token }));
}

/** On the other side (a teamless gun is on none), still fighting, and armed with a ranged weapon. */
function threatens(c: Combatant, figure: Combatant): boolean {
  return opposedSides(c, figure) && isInTheFight(c) && holdsRangedWeapon(c);
}

/** How the readings are measured: the table's rulings, as the attack reads them, and whether GM-only terrain counts. */
export interface CueOptions {
  rules?: RelationRules;
  includeHidden?: boolean;
}

/** What hides him from the gun: the first opaque thing on the EYE line (the cover line runs lower and may cross
 * something that hides nothing). */
function hiderOf(threat: Token, at: Token, solids: Solid[], includeHidden: boolean): string | null {
  const ignores = threat.ignoresTerrain || at.ignoresTerrain;
  return ignores ? null : (firstBlocker(tokenEye(threat), tokenEye(at), solids, { includeHidden })?.label ?? null);
}

/** Each threat's reading of the figure standing at `spot`, in the order the threats came. */
export function readingsAt(
  spot: { x: number; z: number; y?: number },
  figure: Token,
  threats: readonly Threat[],
  solids: Solid[],
  options: CueOptions = {},
): ThreatReading[] {
  refuseRetiredRules(options);
  const at = standingAt(figure, spot);
  return threats.map((t) => readingOf(t, at, solids, options));
}

/** One gun's relation to him where he stands: the attack's own (`relateOne`), so the cue never disagrees with it. */
const relationOf = (threat: Threat, at: Token, solids: Solid[], options: CueOptions) =>
  relateOne(threat.token, at, solids, {
    includeHidden: options.includeHidden ?? false,
    rules: options.rules,
  });

const levelOfRelation = (rel: ReturnType<typeof relateOne>): CueLevel => (rel.blocked ? 'out' : rel.cover.level);

function readingOf(threat: Threat, at: Token, solids: Solid[], options: CueOptions): ThreatReading {
  const rel = relationOf(threat, at, solids, options);
  // The battlefield's one bearing (0 = east, wrapped to 0-360), the facing ring's and the arcs' own.
  const bearing = bearingBetween(at, threat.token);
  const level = levelOfRelation(rel);
  const includeHidden = options.includeHidden ?? false;
  const source = rel.blocked ? hiderOf(threat.token, at, solids, includeHidden) : rel.measurement.coverSource;
  return { threatId: threat.id, name: threat.name, level, source, beyond: rel.measurement.beyond[0] ?? null, bearing };
}

/** The worst cover across the readings - what a reach cell shows - or null when nothing threatens. */
export function worstOf(readings: readonly ThreatReading[]): CueLevel | null {
  return worstLevel(readings.map((r) => r.level));
}

function worstLevel(levels: readonly CueLevel[]): CueLevel | null {
  let worst: CueLevel | null = null;
  for (const level of levels) if (worst === null || CUE_ORDER[level] < CUE_ORDER[worst]) worst = level;
  return worst;
}

/**
 * The movement reach, graded: each tile within the Full band read as its worst cover. Tiles past `maxCost` (the
 * Charge and Run bands) are left out - the reach grades only the Half and Full bands (the user, 2026-10-24).
 * Nothing threatens: an empty map, so the reach looks as it always did.
 */
export function gradeReach(
  tiles: readonly ReachableTile[],
  figure: Token,
  threats: readonly Threat[],
  solids: Solid[],
  { maxCost, tileSize = 1, ...options }: CueOptions & { maxCost: number; tileSize?: number },
): Map<string, CueLevel> {
  refuseRetiredRules(options);
  const graded = new Map<string, CueLevel>();
  if (!threats.length) return graded;
  for (const tile of tiles) {
    if (tile.cost > maxCost) continue;
    // The level alone: a tile's grade never reads what hides him or the bearing, so it never pays for them.
    const at = standingAt(figure, tile);
    const worst = worstLevel(threats.map((t) => levelOfRelation(relationOf(t, at, solids, options))));
    if (worst) graded.set(tileKey(tile.x, tile.z, tileSize), worst);
  }
  return graded;
}
